import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { User, Mail, Lock, AlertCircle, Gift, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';

// Corrections automatiques des domaines fréquents
const DOMAIN_CORRECTIONS = {
  "gmial.com": "gmail.com",
  "gmal.com": "gmail.com",
  "gmai.com": "gmail.com",
  "gamil.com": "gmail.com",
  "gmaill.com": "gmail.com",
  "gmil.com": "gmail.com",
  "gmail.con": "gmail.com",
  "gmail.fr": "gmail.com",
  "hotmial.com": "hotmail.com",
  "hotmal.com": "hotmail.com",
  "hotmail.con": "hotmail.com",
  "hotmaill.com": "hotmail.com",
  "outlok.com": "outlook.com",
  "outllook.com": "outlook.com",
  "outlook.con": "outlook.com",
  "outlool.com": "outlook.com",
  "icloud.con": "icloud.com",
  "iclou.com": "icloud.com",
  "yaho.com": "yahoo.com",
  "yahooo.com": "yahoo.com",
  "yahoo.con": "yahoo.com",
  "yahho.com": "yahoo.com",
  "protonmail.con": "protonmail.com",
  "protonmal.com": "protonmail.com",
  "wanadoo.con": "wanadoo.fr",
  "oange.fr": "orange.fr",
  "oragne.fr": "orange.fr",
  "sfr.con": "sfr.fr",
  "free.con": "free.fr",
  "laposte.con": "laposte.net",
};

const Register = () => {
  const [searchParams] = useSearchParams();
  const vendorCode = searchParams.get('vendor');
  const referralCode = searchParams.get('ref');
  const planName = searchParams.get('plan');
  const planPrice = searchParams.get('price');

  const [formData, setFormData] = useState({
    email: '',
    email_confirm: '',
    full_name: '',
    password: '',
    confirmPassword: '',
    snapchat: '',
    vendor_code: vendorCode || '',
    referral_code: referralCode || ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralInfo, setReferralInfo] = useState(null);
  const [domainSuggestion, setDomainSuggestion] = useState(null);
  const [emailMismatch, setEmailMismatch] = useState(false);
  const [domainValidating, setDomainValidating] = useState(false);
  const [domainValid, setDomainValid] = useState(null);
  const { register, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const emailConfirmRef = useRef(null);
  const domainCheckTimeout = useRef(null);

  useEffect(() => {
    if (referralCode) {
      api.get(`/referral/${referralCode}/info`)
        .then(res => setReferralInfo(res.data))
        .catch(() => setReferralInfo(null));
    }
  }, [referralCode]);

  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'vendor') navigate('/vendor');
      else navigate('/client');
    }
  }, [isAuthenticated, user, navigate]);

  // Vérification domaine email en temps réel (debounced)
  const checkDomainValidity = useCallback((email) => {
    if (domainCheckTimeout.current) clearTimeout(domainCheckTimeout.current);
    
    if (!email || !email.includes('@') || email.split('@')[1].length < 3) {
      setDomainValid(null);
      setDomainValidating(false);
      return;
    }
    
    setDomainValidating(true);
    domainCheckTimeout.current = setTimeout(async () => {
      try {
        const res = await api.post('/auth/validate-email', { email: email.trim().toLowerCase() });
        setDomainValid(res.data.valid);
        if (!res.data.valid) {
          setDomainValid(false);
        }
      } catch {
        setDomainValid(null);
      } finally {
        setDomainValidating(false);
      }
    }, 800);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Auto trim + lowercase pour les champs email
    if (name === 'email' || name === 'email_confirm') {
      processedValue = value.replace(/\s/g, '').toLowerCase();
    }

    setFormData(prev => ({ ...prev, [name]: processedValue }));

    // Vérification domaine auto-correction pour le champ email principal
    if (name === 'email' && processedValue.includes('@')) {
      const [local, domain] = processedValue.split('@');
      const correctedDomain = DOMAIN_CORRECTIONS[domain?.toLowerCase()];
      if (correctedDomain && domain?.toLowerCase() !== correctedDomain) {
        setDomainSuggestion({ original: domain, corrected: correctedDomain, full: `${local}@${correctedDomain}` });
      } else {
        setDomainSuggestion(null);
      }
      // Lancer la vérification DNS
      checkDomainValidity(processedValue);
    }

    // Vérifier la correspondance des emails
    if (name === 'email' || name === 'email_confirm') {
      const emailVal = name === 'email' ? processedValue : formData.email;
      const confirmVal = name === 'email_confirm' ? processedValue : formData.email_confirm;
      if (confirmVal.length > 0) {
        setEmailMismatch(emailVal.trim().toLowerCase() !== confirmVal.trim().toLowerCase());
      } else {
        setEmailMismatch(false);
      }
    }
  };

  const applySuggestion = () => {
    if (domainSuggestion) {
      setFormData(prev => ({ ...prev, email: domainSuggestion.full }));
      setDomainSuggestion(null);
      checkDomainValidity(domainSuggestion.full);
      // Re-vérifier la correspondance
      if (formData.email_confirm.length > 0) {
        setEmailMismatch(domainSuggestion.full.toLowerCase() !== formData.email_confirm.trim().toLowerCase());
      }
    }
  };

  // Bloquer copier/coller sur le champ confirmation
  const handlePasteBlock = (e) => { e.preventDefault(); };
  const handleDropBlock = (e) => { e.preventDefault(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const email = formData.email.trim().toLowerCase();
    const emailConfirm = formData.email_confirm.trim().toLowerCase();

    // Validation double email
    if (email !== emailConfirm) {
      setError('Les adresses email ne correspondent pas. Veuillez vérifier.');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    // Vérifier le domaine si invalide
    if (domainValid === false) {
      setError("Le domaine de votre email semble invalide. Veuillez vérifier l'orthographe.");
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      registerData.email = email;
      registerData.email_confirm = emailConfirm;
      await register(registerData);
      // Rediriger vers la page de vérification avec l'email masqué
      navigate('/verify-email-pending', { state: { email: email } });
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'inscription. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="register-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-[#1A1A1D] p-8 rounded-lg metallic-border cyber-glow">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Inscription</h1>
            <p className="text-[#B8B8BA]">Créez votre compte IPTV4KFR</p>
          </div>

          {referralInfo && (
            <div className="bg-[#00FF88]/10 border border-[#00FF88]/30 rounded-lg p-4 mb-6 text-center">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Gift className="h-5 w-5 text-[#00FF88]" />
                <span className="text-[#00FF88] font-semibold">Parrainage de {referralInfo.referrer_name}</span>
              </div>
              {planName && planPrice && (
                <p className="text-white text-sm">
                  Offre <strong>{planName}</strong> à <span className="text-[#00FFFF] font-bold">{Math.max(0, parseFloat(planPrice) - referralInfo.discount)}€</span>
                  <span className="text-[#6B6B6E] ml-2 line-through">{planPrice}€</span>
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C] rounded-lg p-4 mb-6 flex items-start space-x-3" data-testid="error-message">
              <AlertCircle className="h-5 w-5 text-[#FF3B5C] flex-shrink-0 mt-0.5" />
              <span className="text-[#FF3B5C] text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Nom complet */}
            <div>
              <label className="block text-[#E8E8E8] font-medium mb-2">Nom complet</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B8B8BA]" />
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg pl-12 pr-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none transition-colors"
                  placeholder="Jean Dupont"
                  required
                  data-testid="fullname-input"
                />
              </div>
            </div>

            {/* Email principal */}
            <div>
              <label className="block text-[#E8E8E8] font-medium mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B8B8BA]" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck="false"
                  inputMode="email"
                  className={`w-full bg-[#222226] border rounded-lg pl-12 pr-4 py-3 text-white focus:outline-none transition-colors ${
                    domainValid === false ? 'border-[#FF3B5C] focus:border-[#FF3B5C]' :
                    domainValid === true ? 'border-[#00FF88]/50 focus:border-[#00FF88]' :
                    'border-[#3A3A3D] focus:border-[#00FFFF]'
                  }`}
                  placeholder="votre@email.com"
                  required
                  data-testid="email-input"
                />
                {domainValidating && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B8B8BA] text-xs">Vérification...</span>
                )}
              </div>

              {/* Suggestion de correction de domaine */}
              <AnimatePresence>
                {domainSuggestion && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-2"
                  >
                    <div className="bg-[#FFB800]/10 border border-[#FFB800]/30 rounded-lg p-3 flex items-start space-x-2" data-testid="domain-suggestion">
                      <Info className="h-4 w-4 text-[#FFB800] flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <span className="text-[#FFB800]">Voulez-vous dire </span>
                        <button
                          type="button"
                          onClick={applySuggestion}
                          className="text-[#00FFFF] font-semibold hover:underline"
                          data-testid="apply-suggestion-btn"
                        >
                          {domainSuggestion.corrected}
                        </button>
                        <span className="text-[#FFB800]"> ?</span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Domaine invalide */}
              {domainValid === false && !domainSuggestion && (
                <p className="text-[#FF3B5C] text-xs mt-1" data-testid="domain-invalid-msg">Le domaine email semble invalide.</p>
              )}
            </div>

            {/* Confirmation email */}
            <div>
              <label className="block text-[#E8E8E8] font-medium mb-2">Confirmez votre email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B8B8BA]" />
                <input
                  type="email"
                  name="email_confirm"
                  value={formData.email_confirm}
                  onChange={handleChange}
                  onPaste={handlePasteBlock}
                  onDrop={handleDropBlock}
                  ref={emailConfirmRef}
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck="false"
                  inputMode="email"
                  className={`w-full bg-[#222226] border rounded-lg pl-12 pr-4 py-3 text-white focus:outline-none transition-colors ${
                    emailMismatch ? 'border-[#FF3B5C] focus:border-[#FF3B5C]' :
                    (formData.email_confirm.length > 0 && !emailMismatch) ? 'border-[#00FF88]/50 focus:border-[#00FF88]' :
                    'border-[#3A3A3D] focus:border-[#00FFFF]'
                  }`}
                  placeholder="Retapez votre email"
                  required
                  data-testid="email-confirm-input"
                />
              </div>
              {emailMismatch && (
                <p className="text-[#FF3B5C] text-xs mt-1" data-testid="email-mismatch-msg">Les emails ne correspondent pas.</p>
              )}
            </div>

            {/* Mot de passe */}
            <div>
              <label className="block text-[#E8E8E8] font-medium mb-2">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B8B8BA]" />
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg pl-12 pr-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none transition-colors"
                  placeholder="Minimum 8 caractères"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            {/* Confirmer mot de passe */}
            <div>
              <label className="block text-[#E8E8E8] font-medium mb-2">Confirmer le mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B8B8BA]" />
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg pl-12 pr-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none transition-colors"
                  placeholder="Confirmez votre mot de passe"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            {/* Snapchat */}
            <div>
              <label className="block text-[#E8E8E8] font-medium mb-2 text-sm">Snapchat (recommandé)</label>
              <input
                type="text"
                name="snapchat"
                value={formData.snapchat}
                onChange={handleChange}
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-2 text-white focus:border-[#00FFFF] focus:outline-none transition-colors text-sm"
                placeholder="Votre pseudo Snapchat"
                data-testid="snapchat-input"
              />
            </div>

            {/* Info spam */}
            <div className="bg-[#FFB800]/10 border border-[#FFB800]/20 rounded-lg p-3">
              <p className="text-[#FFB800] text-xs flex items-start gap-2">
                <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>Après inscription, vérifiez votre boîte de réception <strong>et votre dossier Spam</strong>. L'email de vérification peut y atterrir.</span>
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || emailMismatch || domainValid === false}
              className="w-full btn-primary cyber-glow-hover disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-button"
            >
              {loading ? 'Inscription...' : "S'inscrire"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#B8B8BA] text-sm">
              Déjà un compte ?{' '}
              <Link to="/connexion" className="text-[#00FFFF] hover:underline" data-testid="login-link">
                Se connecter
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Register;
